import TimelineEventsAPI from "@/api/Analysis/TimelineEvents";
import { ArtifactsFilterDefinition } from "@/Features/Artifacts";
import { useCaseAnalysis } from "@/Features/CaseAnalysis";
import { EventTypesFilterDefinition } from "@/Features/EventTypes";
import {
  TacticsFilterDefinition,
  TechniquesFilterDefinition,
} from "@/Features/Mitre";
import { useTraceAccountFilterDefinition } from "@/Features/TraceAccounts";
import { FilterDefinition, Operators } from "@monolith-forensics/monolith-ui";
import { useMemo } from "react";
import { TimelineEvent } from "../TimelineEvents.types";
import { EvidenceAPI, EvidenceQuery } from "@/Features";

export const useTimelineFilterDefinitions = () => {
  const { case_id } = useCaseAnalysis();

  const traceAccountFilter = useTraceAccountFilterDefinition({
    query: { case_id },
  });

  const baseFilters: FilterDefinition[] = useMemo(() => {
    const result: FilterDefinition[] = [
      {
        label: "Timestamp",
        dataField: "timestamp",
        inputType: "datetime",
        resolution: "millisecond",
        showTimeZone: "utc",
        operators: [
          Operators.Equals,
          Operators.NotEquals,
          Operators.GreaterThan,
          Operators.LessThan,
          Operators.GreaterThanOrEqual,
          Operators.LessThanOrEqual,
          Operators.Between,
          Operators.NotBetween,
        ],
      },
      {
        label: "UUID",
        pluralLabel: "UUIDs",
        dataField: "uuid",
        inputType: "text",
        operators: [
          Operators.Equals,
          Operators.NotEquals,
          Operators.Contains,
          Operators.DoesNotContain,
        ],
      },
      {
        label: "Created On",
        dataField: "created_on",
        inputType: "datetime",
        resolution: "millisecond",
        showTimeZone: "local",
        operators: [
          Operators.Equals,
          Operators.NotEquals,
          Operators.GreaterThan,
          Operators.LessThan,
          Operators.GreaterThanOrEqual,
          Operators.LessThanOrEqual,
          Operators.Between,
          Operators.NotBetween,
        ],
      },
      {
        label: "Updated On",
        dataField: "updated_on",
        inputType: "datetime",
        resolution: "millisecond",
        showTimeZone: "local",
        operators: [
          Operators.Equals,
          Operators.NotEquals,
          Operators.GreaterThan,
          Operators.LessThan,
          Operators.GreaterThanOrEqual,
          Operators.LessThanOrEqual,
          Operators.Between,
          Operators.NotBetween,
        ],
      },
      {
        label: "Created By",
        pluralLabel: "Created By",
        dataField: "created_by_id",
        inputType: "multiselect",
        operators: [Operators.In, Operators.NIn],
        query: {
          initialPageParam: 1,
          cacheKey: ({ search }) =>
            JSON.stringify(
              TimelineEventsAPI.queryKeys.infiniteList({ case_id })
            ),
          loadPage: async ({ pageParam, search }) => {
            const page = (await TimelineEventsAPI.getUsers({ case_id })) as {
              data: TimelineEvent["created_by"][];
            };

            return {
              items: page.data.map((event) => ({
                label: event.full_name,
                value: event.user_id.toString(),
                data: event,
              })),
              nextPageParam: null, // No pagination for users, so we return null for nextPageParam
            };
          },
        },
      },
      {
        label: "Event Name",
        pluralLabel: "Event Names",
        dataField: "event_name",
        inputType: "text",
        operators: [
          Operators.Equals,
          Operators.NotEquals,
          Operators.Contains,
          Operators.DoesNotContain,
        ],
      },
      {
        label: "Source Evidence",
        pluralLabel: "Items",
        dataField: "source_evidence_uuid",
        inputType: "multiselect",
        operators: [Operators.In, Operators.NIn],
        query: {
          initialPageParam: 1,
          cacheKey: ({ search }) =>
            JSON.stringify(
              EvidenceQuery.queryKeys.infiniteList({ case_id: Number(case_id) })
            ),
          loadPage: async ({ pageParam, search }) => {
            const page = await EvidenceAPI.get({
              case_id: Number(case_id),
            }); // TODO: API returns 1000 items by default, this should be good enough for one case, but we should implement pagination to future proof larger cases

            return {
              items: page.data.map((e) => ({
                label: e.evidence_number,
                value: e.uuid,
                data: e,
              })),
              nextPageParam: null, // No pagination for evidence, so we return null for nextPageParam
            };
          },
        },
      },
      {
        label: "Target Evidence",
        pluralLabel: "Items",
        dataField: "target_evidence_uuid",
        inputType: "multiselect",
        operators: [Operators.In, Operators.NIn],
        query: {
          initialPageParam: 1,
          cacheKey: ({ search }) =>
            JSON.stringify(
              EvidenceQuery.queryKeys.infiniteList({ case_id: Number(case_id) })
            ),
          loadPage: async ({ pageParam, search }) => {
            const page = await EvidenceAPI.get({ case_id: Number(case_id) });
            // TODO: API returns 1000 items by default, this should be good enough for one case, but we should implement pagination to future proof larger cases

            return {
              items: page.data.map((e) => ({
                label: e.evidence_number,
                value: e.uuid,
                data: e,
              })),
              nextPageParam: null,
            };
          },
        },
      },
      EventTypesFilterDefinition,
      ArtifactsFilterDefinition,
      traceAccountFilter,
      TacticsFilterDefinition,
      TechniquesFilterDefinition,
      {
        label: "Description",
        pluralLabel: "Descriptions",
        dataField: "description",
        inputType: "text",
        operators: [Operators.Contains, Operators.DoesNotContain],
      },
    ];
    return result;
  }, [case_id, traceAccountFilter]);

  const filters = baseFilters;

  return filters;
};
